import useRequest, { IAlterarStatusRequest, IDataRequest } from '@hooks/useRequest';
import IFatura from '@model/Fatura';
import ILinha from '@model/Linha';
import { EnumVendedorStatus, IVendedor } from '@model/Vendedor';
import { IVendedorMovimentacao } from '@model/VendedorMovimentacao';
import { IEnum } from '@utils/TypeHelpers';
import React from 'react';
import { IVendedorComissaoFilter, IVendedorComissaoRecord } from './VendedorComissaoService';
import { IVendedorMovimentacaoFilter } from './VendedorMovimentacaoService';

export interface IVendedorFilter {
    vendedorId?: number,
    nome: string,
    cpf: string,
    status: EnumVendedorStatus
}

export interface IVendedorRegistrar {
    nome: string
    cpf: string
    telefone: string
}

export interface IVendedorEditar {
    nome: string
    cpf: string
    telefone: string
}

const controller = '/Vendedor';

export function useVendedorService () {
    const _request = useRequest();

    const carregar = React.useCallback((vendedorId: number) => {
        return _request.get<IVendedor>(`${controller}/Carregar/${vendedorId}`);
    }, [_request]);

    const listar = React.useCallback((request: IDataRequest<IVendedorFilter>) => {
        return _request.getData<IVendedor[]>(`${controller}/Listar`, request);
    }, [_request]);

    const registrar = React.useCallback((request: IVendedorRegistrar) => {
        return _request.post<IVendedor>(`${controller}/Registrar`, request);
    }, [_request]);

    const editar = React.useCallback((vendedorId: number, request: IVendedorEditar) => {
        return _request.post<IVendedor>(`${controller}/Editar/${vendedorId}`, request);
    }, [_request]);

    const alterarStatus = React.useCallback((vendedorId: number, request: IAlterarStatusRequest<EnumVendedorStatus>) => {
        return _request.post<IVendedor>(`${controller}/AlterarStatus/${vendedorId}`, request);
    }, [_request]);

    const listarStatus = React.useCallback(() => {
        return _request.get<IEnum<EnumVendedorStatus>[]>(`${controller}/listarStatus`);
    }, [_request]);

    const listarAtivos = React.useCallback(() => {
        return _request.get<IVendedor[]>(`${controller}/ListarAtivos`);
    }, [_request]);
    
    const listarInventario = React.useCallback((vendedorId: number) => {
        return _request.get<ILinha[]>(`${controller}/ListarInventario/${vendedorId}`);
    }, [_request]);

    const listarMovimentacoes = React.useCallback((vendedorId: number, request: IVendedorMovimentacaoFilter) => {
        return _request.get<IVendedorMovimentacao[]>(`${controller}/ListarMovimentacoes/${vendedorId}`, {
            params: request
        });
    }, [_request]);

    const listarComissoes = React.useCallback((vendedorId: number, request: IVendedorComissaoFilter) => {
        return _request.get<IVendedorComissaoRecord[]>(`${controller}/ListarComissoes/${vendedorId}`, {
            params: request
        });
    }, [_request]);

    const listarInadimplentes = React.useCallback((vendedorId: number) => {
        return _request.get<IFatura[]>(`${controller}/ListarInadimplentes/${vendedorId}`);
    }, [_request]);

    return React.useMemo(() => ({
        listar, registrar, editar, alterarStatus, listarStatus, listarAtivos, carregar, listarInventario, listarMovimentacoes, listarComissoes, listarInadimplentes
    }), [listar, registrar, editar, alterarStatus, listarStatus, listarAtivos, carregar, listarInventario, listarMovimentacoes, listarComissoes, listarInadimplentes]);
}